import {
  Configuration,
  ConfigurationParameters,
  CreateFilterDto,
  FilterDtoV2,
  FiltersControllerApiFactory,
} from '@ecosystem/preference-filters-api';

import axios, { AxiosError } from 'axios';

import {
  createFilterCouldNotBeDeletedResponse,
  createFilterCouldNotBeSavedResponse,
  createFilterCouldNotBeSavedWithNameResponse,
  createFilterDeletedSuccessfullyResponse,
  createFilterIdNotFoundResponse,
  createFilterSavedSuccessfullyResponse,
} from './FilterPersistenceResponseFactory';
import { FilterContext, FilterPersistenceAsyncService } from './FilterPersistenceService';
import { FilterPersistenceResponse, SaveFilterRequest, SaveFiltersObject, UserFilter } from './Types';
import { BASE_PREFERENCE_PATH } from '../../UrlUtils';
import { ErrorStatusResponse } from '../../userPreferencesUtils/Types';
import saveErrorLog, { ErrorTagEnum } from '../../LogUtil';
import { isNotFoundError, isValidationError } from '../../userPreferencesUtils/CheckPreferenceErrorStatusCode';
import { preferencesEnvironment } from '@environments/environment';

export const NEWRON_PRODUCT_KEY = 'RI';
const API_BASE_PATH = BASE_PREFERENCE_PATH;

const persistedToUserFilter: (persisted: FilterDtoV2) => UserFilter = (persisted) => {
  return {
    id: persisted.id,
    name: persisted.name,
    createdAt: new Date(persisted.created_at).getTime(),
    values: Object.values(persisted.data),
    namespace: persisted.context.namespace!,
    permissions: persisted.permissions.map((x) => ({
      user: {
        id: x.user.user_id,
        givenName: x.user.given_name!,
        familyName: x.user.family_name!,
        email: x.user.email!,
      },
      access: x.access,
    })),
    viewed: persisted.viewed,
  };
};

const getFilters = async (userToken: string, clientId: string): Promise<UserFilter[]> => {
  const service = FiltersControllerApiFactory(
    new Configuration({
      accessToken: userToken,
      basePath: preferencesEnvironment.baseUrl,
    } as ConfigurationParameters),
  );

  const result = await service.filtersControllerV2FindAll(
    NEWRON_PRODUCT_KEY,
    undefined,
    undefined,
    `clientId::${clientId}`,
  );
  return (result.data ?? []).map(persistedToUserFilter);
};

const saveFilter = async (
  userToken: string,
  context: FilterContext,
  request: SaveFilterRequest,
): Promise<FilterPersistenceResponse> => {
  const pop: SaveFiltersObject = {
    values: request.values,
  };

  const filter: CreateFilterDto = {
    context: {
      product: NEWRON_PRODUCT_KEY,
      item_group: request.context.itemGroup,
      market: request.context.country,
      namespace: request.context.namespace,
    },
    name: request.name,
    data: pop,
  };

  const service = FiltersControllerApiFactory(
    new Configuration({
      accessToken: userToken,
      basePath: preferencesEnvironment.baseUrl,
    } as ConfigurationParameters),
  );
  try {
    const result = await service.filtersControllerV2Create(filter);
    const userFilter: UserFilter = persistedToUserFilter(result.data);
    return createFilterSavedSuccessfullyResponse(userFilter);
  } catch (e) {
    if (axios.isAxiosError(e) && isValidationError(e as AxiosError<ErrorStatusResponse>)) {
      return createFilterCouldNotBeSavedWithNameResponse();
    }
    saveErrorLog(e, `Failed to save persisted filter ${filter.name}. Service response was ${e}`, {
      category: ErrorTagEnum.ApiError,
    });
    return createFilterCouldNotBeSavedResponse(filter.name);
  }
};

const deleteFilter = async (userToken: string, id: string): Promise<FilterPersistenceResponse> => {
  const service = FiltersControllerApiFactory(
    new Configuration({
      accessToken: userToken,
      basePath: preferencesEnvironment.baseUrl,
    } as ConfigurationParameters),
  );
  try {
    const result = await service.filtersControllerV2DeleteById(id);
    return createFilterDeletedSuccessfullyResponse(result?.data?.name);
  } catch (e) {
    if (axios.isAxiosError(e) && isNotFoundError(e as AxiosError<ErrorStatusResponse>, id)) {
      return createFilterIdNotFoundResponse(id);
    }
    saveErrorLog(e, `Failed to delete persisted filter ${id}. Service response was ${e}`, {
      category: ErrorTagEnum.ApiError,
    });
    return createFilterCouldNotBeDeletedResponse();
  }
};

export const PreferenceAPIFilterPersistenceService: FilterPersistenceAsyncService = {
  getFilters,
  saveFilter,
  deleteFilter,
};
